import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { updateSpecification, type Specification } from '../../api/specification';
import { getCableTypes } from '../../api/cableTypes';
import { useToast } from '../../hooks/useToast';

interface CableSpecEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedCable: Specification | null;
  projectId: number;
  onSuccess?: () => void; // Callback to trigger data reload
}

export default function CableSpecEditModal({ 
  isOpen, 
  onClose, 
  selectedCable, 
  projectId,
  onSuccess
}: CableSpecEditModalProps) {
  const toast = useToast();
  const [mouseDownTarget, setMouseDownTarget] = useState<EventTarget | null>(null);
  const [currentDocumentationUrl, setCurrentDocumentationUrl] = useState<string>('');
  
  const queryClient = useQueryClient();

  const { data: cableTypes = [] } = useQuery({
    queryKey: ['cableTypes'],
    queryFn: getCableTypes
  });

  // Initialize URL when modal opens
  useEffect(() => {
    if (selectedCable) {
      setCurrentDocumentationUrl(selectedCable.documentation_url || '');
    }
  }, [selectedCable]);

  // Update mutation
  const updateCableMutation = useMutation({
    mutationFn: async (data: { id: number; updates: Partial<Specification> }) => {
      await updateSpecification(data.id, data.updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cables', projectId] });
      queryClient.invalidateQueries({ queryKey: ['availableTerminalCables', projectId] });
      toast.success('Кабель успешно обновлен');
      onClose();
      onSuccess?.(); // Call the onSuccess prop
    },
    onError: (error) => {
      console.error('Error updating cable specification:', error);
    }
  });

  // Handle form submission
  const handleEditCable = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedCable) return;
    
    const formData = new FormData(e.currentTarget);
    
    // Generate automatic name based on cable properties
    const wireCount = parseInt(formData.get('wire_count') as string);
    const section = parseFloat(formData.get('section') as string);
    const cableType = formData.get('cable_type') as string;
    const autoName = `${cableType} ${wireCount}×${section}`;
    
    const updates = {
      name: autoName,
      model: autoName,
      manufacturer: formData.get('manufacturer') as string || 'Standard',
      length_meters: parseFloat(formData.get('length_meters') as string) || 100,
      documentation_url: currentDocumentationUrl || undefined,
      device_type_id: selectedCable.device_type_id || 7, // Ensure device_type_id is always set
      properties: {
        wire_count: wireCount,
        section: section,
        cable_type: cableType,
        voltage_rating: '0.66kV',
        fire_safety: 'нг',
        flexibility: formData.get('flexibility') as string || 'rigid'
      }
    };

    updateCableMutation.mutate({ id: selectedCable.id, updates });
  };

  // Close modal on ESC key
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !selectedCable) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) {
          setMouseDownTarget(e.target);
        }
      }}
      onMouseUp={(e) => {
        if (e.target === e.currentTarget && mouseDownTarget === e.target) {
          onClose();
        }
        setMouseDownTarget(null);
      }}
    >
      <div 
        className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            Редактировать кабель {selectedCable ? `id=${selectedCable.id}` : ''}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleEditCable} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="form-label">Производитель</label>
              <input
                name="manufacturer"
                type="text"
                defaultValue={selectedCable.manufacturer || 'Standard'}
                className="form-input"
              />
            </div>
            <div>
              <label className="form-label">Тип кабеля *</label>
              <select 
                name="cable_type" 
                required 
                className="form-input" 
                defaultValue={selectedCable.properties?.cable_type || ''}
              >
                <option value="">Выберите тип...</option>
                {cableTypes.filter(ct => ct.is_active).map(type => (
                  <option key={type.id} value={type.name}>{type.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">Количество жил *</label>
              <input
                name="wire_count"
                type="number"
                required
                min="1"
                max="50"
                defaultValue={selectedCable.properties?.wire_count || 3}
                className="form-input"
              />
            </div>
            <div>
              <label className="form-label">Сечение (мм²) *</label>
              <input
                name="section"
                type="number"
                step="0.01"
                required
                min="0.1"
                defaultValue={selectedCable.properties?.section || 1.5}
                className="form-input"
              />
            </div>


            <div>
              <label className="form-label">Длина (м)</label>
              <input
                name="length_meters"
                type="number"
                step="0.1"
                min="0"
                defaultValue={selectedCable.length_meters || 100}
                className="form-input"
              />
            </div>

            <div>
              <label className="form-label">Гибкость</label>
              <select 
                name="flexibility" 
                className="form-input" 
                defaultValue={selectedCable.properties?.flexibility || 'rigid'}
              >
                <option value="rigid">Жесткий</option>
                <option value="flexible">Гибкий</option>
              </select>
            </div>
          </div>

          <div>
            <label className="form-label">URL документации</label>
            <input
              name="documentation_url"
              type="url"
              value={currentDocumentationUrl}
              onChange={(e) => setCurrentDocumentationUrl(e.target.value)}
              placeholder="https://example.com/docs"
              className="form-input"
            />
            {currentDocumentationUrl && (
              <div className="mt-2">
                <a
                  href={currentDocumentationUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
                >
                  📖 Открыть документацию
                  <span className="text-xs">(откроется в новом окне)</span>
                </a>
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Отмена
            </button>
            <button
              type="submit"
              disabled={updateCableMutation.isPending}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {updateCableMutation.isPending ? 'Сохранение...' : 'Сохранить'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
} 